import { Button, MenuItem, Select, Tooltip } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBread from "../../../components/HeaderBread";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import Layout from "../../../Layout";
import CustomHeaderTitle from "../../../components/constants/CustomHeaderTitle";
import SwitchBar from "../../../components/Pages/SwitchBar";
import SearchField from "../../../components/SearchField";
import CustomizedTable from "../../../components/UI/CustomizedTable";
import { getSeawareDescriptions, syncSeawareDescriptions } from "../../../utils/ApiCalls/MobileAncillary";
import { useDebounce } from "../../../hooks";

const CATEGORIES = ["WIFI", "PGRATUITY", "SHOREX", "EVENT/SHOW", "FNB"];

const CATEGORY_COLORS = {
  WIFI:      { background: "#1976d2", color: "#fff" },
  SHOREX:    { background: "#00796b", color: "#fff" },
  "EVENT/SHOW": { background: "#7b1fa2", color: "#fff" },
  PGRATUITY: { background: "#e65100", color: "#fff" },
  FNB:       { background: "#c62828", color: "#fff" },
};

const categoryChipStyle = (category) => ({
  display: "inline-block",
  padding: "2px 10px",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.4px",
  ...(CATEGORY_COLORS[category] ?? { background: "#9e9e9e", color: "#fff" }),
});

const stripHtml = (html) => {
  if (!html) return "-";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

const TruncatedCell = ({ text, maxLen = 120 }) => {
  const plain = stripHtml(text);
  if (!plain || plain === "-") return <span style={{ color: "#bbb" }}>—</span>;
  if (plain.length <= maxLen) return <span>{plain}</span>;
  return (
    <Tooltip title={plain} placement="top" arrow>
      <span style={{ cursor: "help" }}>{plain.slice(0, maxLen)}…</span>
    </Tooltip>
  );
};

const syncSpinStyle = document.createElement("style");
syncSpinStyle.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
document.head.appendChild(syncSpinStyle);

const SeawareAncillaryCMS = () => {
  const navigate = useNavigate();
  const locationP = useLocation();
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Seaware Ancillary CMS", route: "/shore/mobile-ancillary/seaware-ancillary" },
  ];

  const [active, setActive] = useState("listing");
  const [syncing, setSyncing] = useState(false);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 20
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );
  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    const params = new URLSearchParams(locationP.search);
    setSearch(params.get("search") || "");
  }, [locationP.search]);

  useEffect(() => {
    const params = new URLSearchParams();
    const currentParams = new URLSearchParams(locationP.search);
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (currentParams.toString() !== params.toString()) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [page, pageSize, debouncedSearch, navigate, locationP.search]);

  const columns = [
    {
      title: "Sl",
      key: "idx",
      width: 40,
      render: (_, __, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      title: "Ancillary ID",
      dataIndex: "ancillaryId",
      key: "ancillaryId",
      width: 130,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 110,
      render: (val) => val ? <span style={categoryChipStyle(val)}>{val}</span> : "—",
    },
    {
      title: "Name",
      key: "name",
      children: [
        {
          title: "English",
          dataIndex: "nameEn",
          key: "nameEn",
          width: 160,
          render: (val) => val || <span style={{ color: "#bbb" }}>—</span>,
        },
        {
          title: <span style={{ whiteSpace: "nowrap" }}>Traditional Chinese</span>,
          dataIndex: "nameZhHK",
          key: "nameZhHK",
          width: 160,
          render: (val) => val || <span style={{ color: "#bbb" }}>—</span>,
        },
        {
          title: "Simplified Chinese",
          dataIndex: "nameZhSG",
          key: "nameZhSG",
          width: 160,
          render: (val) => val || <span style={{ color: "#bbb" }}>—</span>,
        },
      ],
    },
    {
      title: "Description",
      key: "description",
      children: [
        {
          title: "English",
          dataIndex: "descriptionEn",
          key: "descriptionEn",
          width: 200,
          render: (val) => <TruncatedCell text={val} />,
        },
        {
          title: <span style={{ whiteSpace: "nowrap" }}>Traditional Chinese</span>,
          dataIndex: "descriptionZhHK",
          key: "descriptionZhHK",
          width: 200,
          render: (val) => <TruncatedCell text={val} />,
        },
        {
          title: "Simplified Chinese",
          dataIndex: "descriptionZhSG",
          key: "descriptionZhSG",
          width: 200,
          render: (val) => <TruncatedCell text={val} />,
        },
      ],
    },
    {
      title: "Synced At",
      dataIndex: "syncedAt",
      key: "syncedAt",
      width: 140,
      render: (val) =>
        val
          ? new Date(val).toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—",
    },
  ];

  const getData = async (pageNum) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = {
      search: debouncedSearch,
      page: pageNum || page,
      limit: pageSize,
      order: "asc",
    };
    if (category) args.category = category;

    const data = await getSeawareDescriptions(args);
    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else {
      setRows(data.data || []);
      setCount(data.count || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    if (active === "listing") {
      getData(page);
    }
  }, [page, pageSize, debouncedSearch, category, active]);

  const handleSyncNow = async () => {
    setSyncing(true);
    const result = await syncSeawareDescriptions();
    if (result.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: result.error, type: "error" },
      });
    } else {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: result.message || "Sync done", type: "success" },
      });
      getData(1);
      setPage(1);
    }
    setSyncing(false);
  };

  return (
    <Layout type={"read_per"} module_data={"Seaware Ancillary CMS"}>
      <CustomHeaderTitle title={"Seaware Ancillary CMS"} />
      <div>
        <div>
          <HeaderBread navigation={navigation} title={"Seaware Ancillary CMS"} />
        </div>
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <SwitchBar
              tabs={[{ title: "Listing", value: "listing" }]}
              activeTab={active}
              setActiveTab={setActive}
            />
            {active === "listing" && (
              <div className="card">
                <div className="card-header two_item">
                  <h5 className="card-title">Seaware Ancillary CMS</h5>
                  <Button
                    variant="contained"
                    startIcon={<SyncIcon style={syncing ? { animation: "spin 1s linear infinite" } : {}} />}
                    onClick={handleSyncNow}
                    disabled={syncing}
                    style={{ background: "#37474f", fontWeight: 500, whiteSpace: "nowrap" }}
                  >
                    {syncing ? "Syncing..." : "Sync Now"}
                  </Button>
                </div>
                <div className="card-body">
                  <div className="table_holder">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        marginBottom: "10px",
                      }}
                    >
                      <Select
                        style={{ width: "160px", height: "40px" }}
                        displayEmpty
                        value={category}
                        onChange={(e) => {
                          setCategory(e.target.value);
                          setPage(1);
                        }}
                        renderValue={category !== "" ? undefined : () => "All Categories"}
                      >
                        <MenuItem value="">All</MenuItem>
                        {CATEGORIES.map((c) => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </Select>

                      <div style={{ maxWidth: 200 }}>
                        <SearchField
                          value={search}
                          SearchValue={() => setPage(1)}
                          setValue={(e) => setSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <CustomizedTable
                      setRows={setRows}
                      setPageSize={setPageSize}
                      setPage={setPage}
                      pageSize={pageSize}
                      page={page}
                      count={count}
                      columns={columns}
                      rows={rows}
                      sortHandler={() => getData(page)}
                      title={"Seaware Ancillary CMS"}
                      scroll={{ x: 1600 }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default SeawareAncillaryCMS;
